import ConfigProvider, { type ThemeConfig } from "antd/lib/config-provider";
import Dropdown from "antd/lib/dropdown";
import theme from "antd/lib/theme";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IUser } from "@sol/sdk";
import { Chevron } from "@sol/uikit/core/icons/Chevron";
import { Button } from "@sol/uikit/general/Button/Button";
import { useRouteBasedMenuState } from "@sol/uikit/navigation/Menu/useRouteBasedMenuState";

import { UserAvatar } from "@/entities/user";

import { MenuItem } from "../lib/getLink";

type Props = {
    currentUser?: IUser;
    dropdownItems: MenuItem[];
    menuState: "collapsed" | "fixed" | "overlay";
};

const activeDropdownClasses = [
    "!text-[color:var(--ant-color-error-text)]",
    "bg-[color:var(--ant-color-error-bg)]",
    "border",
    "border-[color:var(--ant-color-error-base)]",
    "hover:border-[color:var(--ant-color-error-base)]",
    "hover:!text-[color:var(--ant-color-text-base)]",
];

export const SidebarUserDropdown = ({ currentUser, dropdownItems, menuState }: Props) => {
    const [t] = useTranslation();
    const router = useRouter();
    const isCollapsed = menuState === "collapsed";
    const dropdownMenuState = useRouteBasedMenuState(dropdownItems);

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const { token } = theme.useToken();
    const themeConfig: ThemeConfig = useMemo(
        () => ({
            cssVar: { key: "user-dropdown-menu" },
            token: { colorPrimary: token.red },
        }),
        [token],
    );

    useEffect(() => {
        if (isCollapsed) {
            setDropdownOpen(false);
        }
    }, [isCollapsed]);

    const displayedDropdownItems = useMemo(() => {
        return dropdownItems.reduce((acc, item) => {
            if (item) {
                const className = clsx([item.key === router.asPath ? activeDropdownClasses : ""], item.className);
                acc.push({ ...item, className });
            }
            return acc;
        }, [] as MenuItem[]);
    }, [dropdownItems, router.asPath]);

    const dropdownMenuItems = useMemo(() => {
        return [...displayedDropdownItems];
    }, [displayedDropdownItems]);

    return (
        <ConfigProvider theme={themeConfig}>
            <Dropdown
                menu={{ items: dropdownMenuItems, ...dropdownMenuState }}
                trigger={["click"]}
                placement="topLeft"
                overlayClassName="max-w-[200px] [&_:where(.ant-dropdown-menu-root)]:flex [&_:where(.ant-dropdown-menu-root)]:flex-col [&_:where(.ant-dropdown-menu-root)]:gap-ant-xs [&_.ant-dropdown-menu-item]:cursor-pointer px-ant [&_.ant-dropdown-menu-item-group-title]:!p-0"
                open={!isCollapsed && dropdownOpen}
                onOpenChange={setDropdownOpen}
            >
                <Button
                    data-cy="account-menu"
                    type="text"
                    className="mt-auto flex h-fit items-center rounded-none border-x-0 border-t-ant border-b-0 border-ant-color p-3 hover:cursor-pointer"
                >
                    <Typography.Text className="text-ant-sm">
                        <span className="inline-flex items-center gap-3">
                            {currentUser ? <UserAvatar user={currentUser} size={24} className="bg-ant-fill!" /> : null}
                            <span className={clsx([isCollapsed ? "hidden" : "block"])}>
                                {t("component.Navigation.Menu.dropdownMenu.title")}
                            </span>
                        </span>
                    </Typography.Text>
                    <Chevron
                        direction="right"
                        size={16}
                        className={clsx([isCollapsed ? "hidden" : "block", "ml-auto"])}
                    />
                </Button>
            </Dropdown>
        </ConfigProvider>
    );
};
